import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import useScrollBlock from '@hooks/useScrollBlock';
import { Placements } from '@cvent/carina/components/Popover';
import Panel from '@cvent/carina/components/Panel';
import { Keys } from '@tools/KeyConstants';
import { Z_INDEX } from './zIndex';

const PLACEMENTS = {
  BOTTOM_START: Placements.bottomStart,
  BOTTOM_END: Placements.bottomEnd
};

const getFlyoutPosition = ({ placement, placementOffset }) => {
  if (placement === PLACEMENTS.BOTTOM_START) {
    return placementOffset;
  }
  return {
    top: placementOffset.top || 0,
    right: -placementOffset.left || 0
  };
};

const useStyles = ({ placement, placementOffset, zIndex, isMobile }) => {
  const { backgroundColor, size, depth, animation } = useTheme();
  const softShadow = depth.boxShadow.soft;
  const flyoutPosition = getFlyoutPosition({ placement, placementOffset });

  return {
    flyout: {
      position: 'relative',
      zIndex: Z_INDEX.FLY_OUT,
      height: '100%',
      display: 'block'
    },
    flyoutInner: {
      position: 'absolute',
      boxShadow: `${softShadow.color} ${softShadow.xOffset} ${softShadow.yOffset} ${softShadow.blurRadius} ${softShadow.spreadRadius}`,
      backgroundColor: backgroundColor.base,
      borderRadius: size.border.radius.fiveX,
      zIndex,
      transition: isMobile
        ? 'none'
        : `opacity ${animation.delay.fast}, visibility ${animation.delay.fast}`,
      opacity: 0,
      visibility: 'hidden',
      ...flyoutPosition
    },
    fadeIn: {
      visibility: 'visible',
      opacity: 1
    },
    flyoutContainer: {
      position: 'relative'
    },
    spacer: {
      left: '50%',
      bottom: 0,
      transform: 'translate(-50%, 100%)',
      position: 'absolute',
      overflow: 'hidden',
      padding: 0,
      width: '100%',
      zIndex: 400,
      height: placementOffset?.top ? placementOffset?.top : 0
    }
  };
};

/**
 * Component to display content -
 * 1. As a flyout below the trigger component for desktop view
 * 2. As a slide panel for mobile view
 */
const Flyout = props => {
  const {
    trigger,
    children,
    testID,
    placement,
    placementOffset,
    zIndex,
    isVisible,
    toggleVisible,
    triggerMethod = 'CLICK',
    hasSpacerAfterTrigger
  } = props;
  const flyoutContainerRef = useRef();
  const flyoutRef = useRef();
  const toggleVisibleRef = useRef(toggleVisible);
  const { isMobile } = useBreakpoints();
  const [blockScroll, allowScroll] = useScrollBlock();
  // const { animation } = useTheme();
  const styles = useStyles({ placement, placementOffset, zIndex, isMobile });

  const handlers = useMemo(() => {
    // Memoize these handlers so the allowScroll/blockScroll effect can have one less dependency
    if (isMobile) {
      return {
        handleClickOutside: event => {
          if (!flyoutContainerRef?.current?.contains(event.target)) {
            if (typeof toggleVisibleRef.current === 'function')
              toggleVisibleRef.current(false);
          }
        },
        onKeyDown: event => {
          if (event.key === Keys.ESCAPE) {
            if (typeof toggleVisibleRef.current === 'function')
              toggleVisibleRef.current(false);
          }
        }
      };
    }
    return {
      handleClickOutside: event => {
        if (!flyoutContainerRef?.current?.contains(event.target)) {
          toggleVisibleRef.current(false);
        }
      },
      onKeyDown: event => {
        if (event.key === Keys.ESCAPE) {
          toggleVisibleRef.current(false);
        }
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('click', handlers.handleClickOutside);
      document.addEventListener('keydown', handlers.onKeyDown);
      blockScroll();
    } else {
      document.removeEventListener('click', handlers.handleClickOutside);
      document.removeEventListener('keydown', handlers.onKeyDown);
      allowScroll();
    }
    return () => {
      document.removeEventListener('click', handlers.handleClickOutside);
      document.removeEventListener('keydown', handlers.onKeyDown);
      allowScroll();
    };
  }, [
    isVisible,
    handlers.handleClickOutside,
    handlers.onKeyDown,
    allowScroll,
    blockScroll
  ]);

  // Setting ref is technically a side-effect
  useEffect(() => {
    toggleVisibleRef.current = toggleVisible;
  }, [toggleVisible]);

  const component =
    typeof children === 'function'
      ? children({ toggleOpen: () => toggleVisible(!isVisible) })
      : children;

  return (
    <div
      css={styles.flyoutContainer}
      onMouseEnter={
        triggerMethod === 'HOVER'
          ? () => {
              if (!isVisible) {
                toggleVisible(true);
              }
            }
          : null
      }
      onMouseLeave={
        triggerMethod === 'HOVER' ? () => toggleVisible(false) : null
      }
      ref={flyoutContainerRef}
      {...injectTestId('flyout-container')}
    >
      <div>
        {trigger({
          toggleOpen:
            triggerMethod === 'HOVER'
              ? () => toggleVisible(true)
              : () => toggleVisible(!isVisible),
          isVisible
        })}
      </div>
      {hasSpacerAfterTrigger ? <div css={styles.spacer} /> : null}
      {isMobile ? (
        <div ref={flyoutRef}>
          <Panel elevated isOpen={isVisible} testID={`${testID}-panel`}>
            {component}
          </Panel>
        </div>
      ) : (
        <div css={styles.flyout} {...injectTestId(testID)}>
          <div
            ref={flyoutRef}
            css={[styles.flyoutInner, isVisible ? styles.fadeIn : {}]}
          >
            {component}
          </div>
        </div>
      )}
    </div>
  );
};

Flyout.propTypes = {
  trigger: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  placement: PropTypes.oneOf([PLACEMENTS.BOTTOM_START, PLACEMENTS.BOTTOM_END]),
  placementOffset: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number
  }),
  testID: PropTypes.string,
  zIndex: PropTypes.number,
  /* parent component state to keep track of visibility of children (notificationFlyout) */
  isVisible: PropTypes.bool.isRequired,
  toggleVisible: PropTypes.func.isRequired,
  triggerMethod: PropTypes.string,
  hasSpacerAfterTrigger: PropTypes.bool
};

Flyout.defaultProps = {
  testID: 'flyout',
  placement: PLACEMENTS.BOTTOM_START,
  placementOffset: {},
  zIndex: 1,
  hasSpacerAfterTrigger: false
};

export default Flyout;
